import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Building2, DoorOpen, CalendarDays, Users, MapPin, Pencil } from 'lucide-react';
import { format } from 'date-fns';

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/properties/${id}`),
      api.get('/property-owners', { params: { property_id: id } })
    ]).then(([p, o]) => {
      setProperty(p.data);
      setOwners(o.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;
  if (!property) return <div className="text-center py-12 text-slate-400">Property not found</div>;

  const statusColors = {
    available: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    unavailable: 'bg-slate-100 text-slate-600',
    maintenance: 'bg-amber-50 text-amber-700 border-amber-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-slate-100 text-slate-600',
  };

  const resStatusColors = {
    confirmed: 'bg-blue-50 text-blue-700', pending: 'bg-amber-50 text-amber-700',
    cancelled: 'bg-red-50 text-red-700', checked_in: 'bg-emerald-50 text-emerald-700',
    checked_out: 'bg-slate-100 text-slate-600',
  };

  const units = property.units || [];

  return (
    <div data-testid="property-detail-page">
      {/* Back + Header */}
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/properties')} className="mb-4 text-slate-600" data-testid="back-to-properties-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Properties
        </Button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold font-heading text-slate-900 tracking-tight" data-testid="property-detail-name">{property.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={statusColors[property.status] || ''} data-testid="property-detail-status">{property.status}</Badge>
              <span className="text-sm text-slate-500 capitalize">{property.property_type}</span>
              {property.address?.city && (
                <span className="flex items-center gap-1 text-sm text-slate-500">
                  <MapPin className="w-3.5 h-3.5" />{property.address.city}{property.address.country ? `, ${property.address.country}` : ''}
                </span>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/properties')} data-testid="edit-property-detail-btn">
            <Pencil className="w-4 h-4 mr-2" />Edit
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card" data-testid="property-units-count">
          <div className="flex items-center gap-2 text-blue-600 mb-1"><DoorOpen className="w-4 h-4" /><span className="text-xs font-medium">Units</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">{units.length}</p>
        </div>
        <div className="stat-card" data-testid="property-active-res">
          <div className="flex items-center gap-2 text-emerald-600 mb-1"><CalendarDays className="w-4 h-4" /><span className="text-xs font-medium">Active Reservations</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">
            {units.reduce((sum, u) => sum + (u.reservations?.filter(r => ['confirmed', 'checked_in'].includes(r.status)).length || 0), 0)}
          </p>
        </div>
        <div className="stat-card" data-testid="property-owners-count">
          <div className="flex items-center gap-2 text-purple-600 mb-1"><Users className="w-4 h-4" /><span className="text-xs font-medium">Owners</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">{owners.length}</p>
        </div>
        <div className="stat-card" data-testid="property-avg-price">
          <div className="flex items-center gap-2 text-amber-600 mb-1"><Building2 className="w-4 h-4" /><span className="text-xs font-medium">Avg. Price</span></div>
          <p className="text-2xl font-semibold font-heading text-slate-900">
            R{units.length > 0 ? Math.round(units.reduce((s, u) => s + (u.base_price || 0), 0) / units.length).toLocaleString() : 0}
          </p>
        </div>
      </div>

      {/* Info + Address */}
      {(property.description || property.address?.street) && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {property.description && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-2">Description</h3>
                <p className="text-sm text-slate-600">{property.description}</p>
              </div>
            )}
            {property.address && (
              <div>
                <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-2">Address</h3>
                <div className="text-sm text-slate-600 space-y-1">
                  {property.address.street && <p>{property.address.street}</p>}
                  <p>{[property.address.city, property.address.state, property.address.zip].filter(Boolean).join(', ')}</p>
                  {property.address.country && <p>{property.address.country}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <Tabs defaultValue="units" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="units" data-testid="prop-tab-units">Units ({units.length})</TabsTrigger>
          <TabsTrigger value="owners" data-testid="prop-tab-owners">Owners ({owners.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="units">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {units.map(unit => (
              <div key={unit.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md hover:border-slate-300 transition-all duration-200" data-testid={`prop-unit-card-${unit.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-medium text-slate-900">{unit.name}</h4>
                    <p className="text-xs text-slate-500">Unit {unit.unit_number}</p>
                  </div>
                  <Badge className={statusColors[unit.status] || ''} variant="outline">{unit.status}</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center mb-3">
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-lg font-semibold text-slate-900">{unit.bedrooms}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Beds</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-lg font-semibold text-slate-900">{unit.bathrooms}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Baths</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-lg font-semibold text-slate-900">{unit.max_guests}</p>
                    <p className="text-[10px] text-slate-500 uppercase">Guests</p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-900">R{(unit.base_price || 0).toLocaleString()}<span className="text-xs text-slate-500 font-normal"> /night</span></span>
                  <span className="text-xs text-slate-500">Clean: R{(unit.cleaning_fee || 0).toLocaleString()}</span>
                </div>
                {unit.amenities?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {unit.amenities.slice(0, 4).map(a => (
                      <span key={a} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{a}</span>
                    ))}
                    {unit.amenities.length > 4 && <span className="text-[10px] text-slate-400">+{unit.amenities.length - 4}</span>}
                  </div>
                )}
              </div>
            ))}
            {units.length === 0 && <p className="col-span-3 text-center py-8 text-slate-400">No units for this property</p>}
          </div>
        </TabsContent>

        <TabsContent value="owners">
          <div className="table-wrap">
            <table className="w-full data-table min-w-[550px]">
              <thead><tr><th>Owner</th><th>Email</th><th>Ownership %</th><th>Management Fee %</th><th>Status</th></tr></thead>
              <tbody>
                {owners.map(o => (
                  <tr key={o.id} data-testid={`prop-owner-row-${o.id}`}>
                    <td className="font-medium text-slate-900">{o.owner_name}</td>
                    <td>{o.owner_email}</td>
                    <td>{o.ownership_percentage}%</td>
                    <td>{o.management_fee_percentage}%</td>
                    <td><Badge className={o.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}>{o.is_active ? 'Active' : 'Inactive'}</Badge></td>
                  </tr>
                ))}
                {owners.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-400">No owners assigned</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
